'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { ProductLogoMediumDevices } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { isForbiddenError, LedgerForbiddenState } from '../-shared/-components/LedgerForbiddenState';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useDeviceLedger } from '../-shared/-query';
const DEVICES_PATH = '/individual/portal/lookup/devices';
const LOOKUP_PATH = '/individual/portal/lookup';
const devicesSearchSchema = z.object({
  scope: z.enum(['self']).catch('self'),
  id: z.string().optional(),
  pageIndex: z.coerce.number().int().min(0).catch(0),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20)
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/devices/')({
  validateSearch: devicesSearchSchema,
  component: DevicesLookupPage
});
type DeviceLedgerItem = NonNullable<ReturnType<typeof useDeviceLedger>['data']>['items'][number];
function DevicesLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id,
    pageIndex,
    pageSize
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [freeText, setFreeText] = useState('');
  const debouncedSetFreeText = useDebouncedCallback((text: string) => {
    setFreeText(text);
    // 検索語変更時は 1 ページ目に戻す（存在しないページで空表示になるのを防ぐ）。
    void navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0
      })
    });
  }, []);
  const {
    data,
    isPending,
    isError,
    error
  } = useDeviceLedger({
    page: pageIndex + 1,
    pageSize,
    scope,
    freeText: freeText === '' ? undefined : freeText
  });
  const selected = id != null ? data?.items.find(d => d.id === id) ?? null : null;
  if (isError && isForbiddenError(error)) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('devices.title')
      }]} Logo={ProductLogoMediumDevices} title={t('devices.title')} />
        <LedgerContent>
          <LedgerForbiddenState onBack={() => void navigate({
          to: LOOKUP_PATH
        })} />
        </LedgerContent>
      </>;
  }
  if (isError) {
    throw error;
  }
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('devices.title'),
        link: {
          to: DEVICES_PATH,
          search: {
            scope,
            pageIndex,
            pageSize
          }
        }
      }, {
        label: selected.name
      }]} Logo={ProductLogoMediumDevices} title={selected.name} subtitle={selected.assetTag} />
        <LedgerContent>
          <ResourceDetailCard title={t('devices.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('devices.detail.genre')} value={selected.genre} />
              <Field label={t('devices.detail.model')} value={selected.model ?? '—'} />
              <Field label={t('devices.detail.assetTag')} value={selected.assetTag ?? '—'} />
              <Field label={t('devices.detail.deviceTag')} value={selected.deviceTag ?? '—'} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<DeviceLedgerItem>({
    id: 'name',
    header: t('devices.headers.name'),
    cell: d => <Text variant="label">{d.name}</Text>
  }), ledgerColumn<DeviceLedgerItem>({
    id: 'genre',
    header: t('devices.headers.genre'),
    cell: d => d.genre
  }), ledgerColumn<DeviceLedgerItem>({
    id: 'model',
    header: t('devices.headers.model'),
    cell: d => d.model ?? '—'
  }), ledgerColumn<DeviceLedgerItem>({
    id: 'assetTag',
    header: t('devices.headers.assetTag'),
    cell: d => d.assetTag ?? '—'
  }), ledgerColumn<DeviceLedgerItem>({
    id: 'deviceTag',
    header: t('devices.headers.deviceTag'),
    cell: d => d.deviceTag ?? '—'
  })];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('devices.title')
    }]} Logo={ProductLogoMediumDevices} title={t('devices.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<DeviceLedgerItem> fillHeight manualPagination columns={columns} data={data?.items} isPending={isPending} pageCount={data?.maxPage ?? 0} totalCount={data?.count} pagination={{
        pageIndex,
        pageSize
      }} onPaginationChange={updater => {
        const next = typeof updater === 'function' ? updater({
          pageIndex,
          pageSize
        }) : updater;
        void navigate({
          search: prev => ({
            ...prev,
            pageIndex: next.pageIndex,
            pageSize: next.pageSize
          })
        });
      }} onSearchTextChange={debouncedSetFreeText} getRowId={d => d.id} searchPlaceholder={t('common.search')} onRowSelect={d => void navigate({
        search: prev => ({
          ...prev,
          id: d.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}